/**
 * Browser-based Word Document Generator
 * Uses docxtemplater to generate IPA reports in the browser
 */

import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import ImageModule from 'docxtemplater-image-module-free';

/**
 * ROI Calculation Helper Functions
 */

// Parse time string to seconds
function parseTimeToSeconds(timeStr) {
  if (!timeStr) return 0;

  const numMatch = timeStr.match(/[\d.]+/);
  if (!numMatch) return 0;

  const value = parseFloat(numMatch[0]);
  const lowerStr = timeStr.toLowerCase();

  if (lowerStr.includes('hour') || lowerStr.includes('hr') || lowerStr.includes('h')) {
    return value * 3600;
  } else if (lowerStr.includes('min') || lowerStr.includes('m')) {
    return value * 60;
  } else if (lowerStr.includes('sec') || lowerStr.includes('s')) {
    return value;
  }

  return value;
}

// Calculate Automation w/ Probability
function calculateAutomationWithProbability(probability, automationPotential) {
  if (!probability || !automationPotential) return '0%';

  const probValue = parseFloat(probability) / 100;
  const autoValue = parseFloat(automationPotential) / 100;
  const result = probValue * autoValue * 100;

  return `${result.toFixed(1)}%`;
}

// Calculate Total Interactions
function calculateTotalInteractions(numberOfInteractions, automationWProbability) {
  if (!numberOfInteractions || !automationWProbability) return '0';

  const interactions = parseFloat(numberOfInteractions);
  const percentage = parseFloat(automationWProbability) / 100;
  const result = interactions * percentage;

  return result.toFixed(0);
}

// Calculate Total Time Saved (in seconds)
function calculateTotalTimeSaved(totalInteractions, avgTimePerInteraction) {
  if (!totalInteractions || !avgTimePerInteraction) return '0';

  const interactions = parseFloat(totalInteractions);
  const timeInSeconds = parseTimeToSeconds(avgTimePerInteraction);
  const result = interactions * timeInSeconds;

  return result.toFixed(0);
}

// Calculate Savings per Period
function calculateSavingsPerPeriod(totalTimeSaved, costPerAgent) {
  if (!totalTimeSaved || !costPerAgent) return '$0.00';

  const timeInHours = parseFloat(totalTimeSaved) / 3600;
  const savings = timeInHours * costPerAgent;

  return `$${savings.toFixed(2)}`;
}

/**
 * Generate ROI table data for Word template
 */
function generateROITableData(processes, costPerAgent = 25) {
  const cases = ['Conservative', 'Likely', 'Optimistic'];
  const defaultProbabilities = {
    'Conservative': '65%',
    'Likely': '80%',
    'Optimistic': '95%'
  };
  const defaultValues = {
    automation_area: 'Self-service',
    automation_potential: '80%',
    number_of_interactions: '10000',
    avg_time_per_interaction: '30 sec'
  };

  const roiTableRows = [];
  const totalSavings = { Conservative: 0, Likely: 0, Optimistic: 0 };

  processes.forEach(process => {
    cases.forEach(caseType => {
      const caseData = process.roi_cases?.[caseType] || {};

      const automationArea = caseData.automation_area || defaultValues.automation_area;
      const automationPotential = caseData.automation_potential || defaultValues.automation_potential;
      const probability = caseData.probability || defaultProbabilities[caseType];
      const numberOfInteractions = caseData.number_of_interactions || defaultValues.number_of_interactions;
      const avgTimePerInteraction = caseData.avg_time_per_interaction || defaultValues.avg_time_per_interaction;

      const automationWProbability = calculateAutomationWithProbability(probability, automationPotential);
      const totalInteractions = calculateTotalInteractions(numberOfInteractions, automationWProbability);
      const totalTimeSaved = calculateTotalTimeSaved(totalInteractions, avgTimePerInteraction);
      const savingsPerPeriod = calculateSavingsPerPeriod(totalTimeSaved, costPerAgent);

      const timeInHours = parseFloat(totalTimeSaved) / 3600;
      const savings = timeInHours * costPerAgent;
      totalSavings[caseType] += savings;

      roiTableRows.push({
        process_id: process.id,
        process_name: process.name,
        automation_area: automationArea,
        automation_potential: automationPotential,
        case: caseType,
        probability: probability,
        automation_w_probability: automationWProbability,
        number_of_interactions: numberOfInteractions,
        total_interactions: totalInteractions,
        avg_time_per_interaction: avgTimePerInteraction,
        total_time_saved: `${totalTimeSaved} sec`,
        savings_per_period: savingsPerPeriod
      });
    });
  });

  const totalSavingsSummary = [
    { case: 'Conservative', total_savings: `$${totalSavings.Conservative.toFixed(2)}` },
    { case: 'Likely', total_savings: `$${totalSavings.Likely.toFixed(2)}` },
    { case: 'Optimistic', total_savings: `$${totalSavings.Optimistic.toFixed(2)}` }
  ];

  return {
    roi_table_rows: roiTableRows,
    roi_total_savings: totalSavingsSummary
  };
}

/**
 * Convert base64 to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Generate Word document using docxtemplater in the browser
 */
export async function generateWordDocument(context, processes, chartBase64, templateArrayBuffer) {
  const today = new Date();
  const todayFormatted = today.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const todayNum = today.toISOString().split('T')[0];
  const author = 'IPA Report Generator';

  // Load template
  const zip = new PizZip(templateArrayBuffer);

  // Convert chart base64 to ArrayBuffer
  const chartBuffer = base64ToArrayBuffer(chartBase64);

  // Configure image module
  const imageOpts = {
    centered: false,
    getImage: (tagValue) => {
      return chartBuffer;
    },
    getSize: () => {
      return [576, 432]; // 6 inches wide, 4.5 inches tall
    }
  };

  const imageModule = new ImageModule(imageOpts);

  // Create docxtemplater instance
  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    modules: [imageModule]
  });

  // Generate ROI table data
  const costPerAgent = context.cost_per_agent || 25;
  const roiData = generateROITableData(processes, costPerAgent);

  // Prepare template data
  const templateData = {
    CUSTOMER: context.customer || 'Unknown',
    VERTICAL: context.vertical || '',
    LOB: context.lob || '',
    DATE: todayFormatted,
    proposal_team: context.proposal_team || [],
    revision: [
      { version: '0.1', date: todayNum, name: author, notes: 'First Internal Release' },
      { version: '1.0', date: todayNum, name: author, notes: 'First Client Release' }
    ],
    INTRODUCTION: context.introduction || '',
    APPROACH: context.approach || '',
    proc_list: processes,
    complexity_chart: 'image',
    RECOMMENDED_APPROACH: context.recommended_approach || '',
    roi_table_rows: roiData.roi_table_rows,
    roi_total_savings: roiData.roi_total_savings,
    cost_per_agent: costPerAgent
  };

  console.log('[documentGenerator] Template data prepared:', {
    hasCustomer: !!templateData.CUSTOMER,
    processCount: templateData.proc_list.length,
    roiTableRowCount: templateData.roi_table_rows.length
  });

  // Render document
  doc.render(templateData);

  // Generate output blob
  const outputBlob = doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    compression: 'DEFLATE'
  });

  return outputBlob;
}
